import React, { useState } from 'react';
import { Bot, Lightbulb, Clock, Zap, X } from 'lucide-react';
import { useAI } from '../../hooks/useAI';
import type { Todo } from '../../types';

interface AIQuickActionsProps {
  todo: Todo;
  onPriorityChanged?: (priority: string) => void;
  onDeadlineChanged?: (deadline: string) => void;
  className?: string;
}

export const AIQuickActions: React.FC<AIQuickActionsProps> = ({
  todo,
  onPriorityChanged,
  onDeadlineChanged,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastResponse, setLastResponse] = useState<string | null>(null);
  const { isLoading, sendMessage } = useAI();

  const handleQuickAction = async (prompt: string, onResult?: (result: string) => void) => {
    const fullPrompt = `Regarding my task "${todo.title}": ${prompt}`;
    const response = await sendMessage(fullPrompt);
    if (response) {
      setLastResponse(response.content);
      onResult?.(response.content);
    }
  };

  const quickActions = [
    {
      icon: Clock,
      label: 'Priority',
      action: () => handleQuickAction("Help me determine the right priority for this task", (response) => {
        const priorityMatch = response.toLowerCase().match(/\b(high|medium|low)\b/);
        if (priorityMatch) {
          onPriorityChanged?.(priorityMatch[1]);
        }
      }),
      description: 'Suggest priority'
    },
    {
      icon: Zap,
      label: 'Motivate',
      action: () => handleQuickAction("Give me motivation and tips to complete this task"),
      description: 'Get motivation'
    },
    {
      icon: Lightbulb,
      label: 'Deadline',
      action: () => handleQuickAction("Suggest a realistic deadline for this task", (response) => {
        const dateMatch = response.match(/\b\d{4}-\d{2}-\d{2}\b/);
        if (dateMatch) {
          onDeadlineChanged?.(dateMatch[0]);
        }
      }),
      description: 'Suggest deadline'
    }
  ];

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={`p-1 rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors ${className}`}
        title="AI Quick Help"
        disabled={isLoading}
      >
        <Bot className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="absolute right-0 top-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-48">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Bot className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-900">AI Assist</span>
          </div>
          <button
            onClick={() => {
              setIsExpanded(false);
              setLastResponse(null);
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              disabled={isLoading}
              className="flex flex-col items-center p-2 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={action.description}
            >
              <action.icon className="w-4 h-4 text-gray-600 mb-1" />
              <span className="text-xs text-gray-700">{action.label}</span>
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Thinking...</span>
          </div>
        )}

        {lastResponse && !isLoading && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-600 mb-1">AI Suggestion:</p>
            <div className="text-sm text-gray-800 bg-gray-50 rounded-md p-2 max-h-32 overflow-y-auto">
              {lastResponse}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
